import type { Pence } from './money.js';
import type { Menu } from './menu.js';
import { priceBag, type BagLine, type PricedLine } from './pricing.js';
import { pointsEarned, type RewardsRules } from './points.js';
import { splitPayment } from './credit.js';
import { discountForOffer, type Offer } from './offers.js';

export interface CheckoutInput {
  menu: Menu;
  lines: readonly BagLine[];
  /** The app's simple preview offer (amount off a main). The server passes `discountPence` from the engine instead. */
  offer?: Offer | null | undefined;
  /** Money off worked out by the offers engine. Wins over `offer` when given. */
  discountPence?: number | undefined;
  useCredit: boolean;
  creditBalancePence: Pence;
  pointsBalance: number;
  /** Future double-points hours. 1 at launch. */
  pointsMultiplier?: number | undefined;
  /** Fixed bonus points from an offer (added on collection, like the rest). */
  pointsBonus?: number | undefined;
  /** The site's Rewards Club numbers (defaults to the launch constants). */
  rewards?: RewardsRules | undefined;
}

export interface CheckoutTotals {
  lines: PricedLine[];
  subtotalPence: Pence;
  discountPence: Pence;
  totalPence: Pence;
  creditPence: Pence;
  cardPence: Pence;
  /** Points this order will add on collection. */
  pointsToEarn: number;
  /** Points reserved by rewards in the bag, spent on collection. */
  pointsToSpend: number;
  /** Rewards club balance after collection. */
  pointsAfter: number;
}

export class CheckoutError extends Error {
  override name = 'CheckoutError';
  constructor(message: string, public readonly code: string) {
    super(message);
  }
}

/**
 * The one place that turns a bag into money. Runs on the server.
 * Order of operations: price lines -> apply code discount -> split credit/card -> points.
 */
export function computeCheckout(input: CheckoutInput): CheckoutTotals {
  if (input.lines.length === 0) throw new CheckoutError('Your bag is empty', 'empty_bag');
  const bag = priceBag(input.menu, input.lines);
  if (bag.pointsReserved > input.pointsBalance) {
    throw new CheckoutError('Not enough points for those rewards', 'insufficient_points');
  }
  const discountPence = input.discountPence !== undefined ? Math.max(0, Math.min(input.discountPence, bag.subtotalPence)) : input.offer ? discountForOffer(input.offer, bag.lines) : 0;
  if (!Number.isInteger(discountPence)) throw new CheckoutError('Bad discount', 'bad_discount');
  const totalPence = bag.subtotalPence - discountPence;
  if (totalPence < 0) throw new CheckoutError('Discount exceeds the order', 'negative_total');
  const { creditPence, cardPence } = splitPayment(totalPence, input.creditBalancePence, input.useCredit);
  const bonus = input.pointsBonus ?? 0;
  if (!Number.isInteger(bonus) || bonus < 0) throw new CheckoutError('Bad bonus points', 'bad_bonus');
  const pointsToEarn = pointsEarned({ creditPence, cardPence }, input.pointsMultiplier ?? 1, input.rewards) + (totalPence > 0 || bonus === 0 ? bonus : 0);
  return {
    lines: bag.lines,
    subtotalPence: bag.subtotalPence,
    discountPence,
    totalPence,
    creditPence,
    cardPence,
    pointsToEarn,
    pointsToSpend: bag.pointsReserved,
    pointsAfter: input.pointsBalance - bag.pointsReserved + pointsToEarn,
  };
}
